import { IFeatures } from "../../domain/brand.model";
import { CountBadgeType, NotificationCounters } from "./notifications.model";

// SAN-1431 (T4.4) — which counter a nav menu item shows. Set on
// INavLinkNew.badgeKey; the menu's own featureKey gating still applies.
export type NavBadgeKey = 'connections' | 'communityWall' | 'challenges' | 'programs' | 'events' | 'jobs';

export interface NavBadge {
  count: number;
  type: CountBadgeType;
  label: string;
  // SAN-1431 — where a click on the badged menu item lands (the list already
  // filtered to what the badge counts). None → the item's own route.
  route?: string;
}

export function navBadgeFor(counters: NotificationCounters | null, key: NavBadgeKey): NavBadge | null {
  if (!counters || !key) return null;
  // Mode A: live counts, outlined, not in the bell. Mode B: orange "unseen".
  const opportunityType: CountBadgeType = counters.opportunities?.mode === 'A' ? 'live' : 'new';
  switch (key) {
    case 'connections':
      return { count: counters.connections, type: 'action', label: 'pending connection requests', route: '/connections/pending-requests' };
    case 'communityWall':
      return { count: counters.communityWall, type: 'new', label: 'new posts since your last visit' };
    case 'challenges':
      return { count: counters.opportunities?.challenges, type: opportunityType, label: opportunityLabel(counters, 'challenges') };
    case 'programs':
      return { count: counters.opportunities?.programs, type: opportunityType, label: opportunityLabel(counters, 'programs') };
    case 'events':
      return { count: counters.opportunities?.events, type: opportunityType, label: opportunityLabel(counters, 'events') };
    case 'jobs':
      return { count: counters.jobs?.total, type: 'action', label: 'new job applications' };
    default:
      return null;
  }
}

function opportunityLabel(counters: NotificationCounters, noun: string): string {
  return counters.opportunities?.mode === 'A' ? `open ${noun}` : `new ${noun} you haven't opened`;
}

export interface NavBadgeMaps {
  // keyed by INavLinkNew.badgeKey
  navBadges: { [key: string]: NavBadge };
  // keyed by parent menu id: sum of its visible children's badges
  parentBadges: { [id: string]: NavBadge };
}

const NAV_BADGE_KEYS: NavBadgeKey[] = ['connections', 'communityWall', 'challenges', 'programs', 'events', 'jobs'];

// SAN-1478 — the in-app category that mutes each badge. Opportunity badges
// (challenges / programs / events) are never muted (D-8).
const MUTED_BY: { [key in NavBadgeKey]?: string } = {
  connections: 'connectionRequests',
  communityWall: 'communityPosts',
  jobs: 'jobApplications',
};

function isBadgeMuted(counters: NotificationCounters, key: NavBadgeKey): boolean {
  return !!MUTED_BY[key] && (counters.inAppMuted || []).includes(MUTED_BY[key]);
}

/** Empty maps (no badge renders) unless notification_centre_enabled is on. */
export function computeNavBadges(
  counters: NotificationCounters | null,
  navItems: Array<{ id: string; children?: Array<{ badgeKey?: NavBadgeKey; featureKey?: keyof IFeatures }> }>,
  features: Partial<IFeatures> | undefined,
): NavBadgeMaps {
  const result: NavBadgeMaps = { navBadges: {}, parentBadges: {} };
  if (features?.notification_centre_enabled !== true || !counters) return result;

  NAV_BADGE_KEYS.forEach((key) => {
    if (isBadgeMuted(counters, key)) return;
    const badge = navBadgeFor(counters, key);
    if (badge?.count > 0) result.navBadges[key] = badge;
  });

  (navItems || []).forEach((item) => {
    const childBadges = (item.children || [])
      .filter((child) => child.badgeKey && result.navBadges[child.badgeKey]
        && (!child.featureKey || features?.[child.featureKey]))
      .map((child) => result.navBadges[child.badgeKey]);
    if (childBadges.length) {
      result.parentBadges[item.id] = {
        count: childBadges.reduce((sum, b) => sum + b.count, 0),
        // Red wins: the parent needs action if any child does.
        type: childBadges.some((b) => b.type === 'action') ? 'action' : childBadges[0].type,
        // SAN-1461 — the parent count is a total, so name each part:
        // "4 pending connection requests, 3 new posts since your last visit".
        label: childBadges.map((b) => `${b.count} ${b.label}`).join(', '),
      };
    }
  });
  return result;
}
